import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  FeatureCategory,
  FeaturePriority,
  FeatureRequest,
  FeatureStats,
  FeatureStatus,
} from '@/types';

export interface FeatureFilters {
  status?: FeatureStatus[];
  category?: FeatureCategory[];
  priority?: FeaturePriority[];
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useFeatureRequests(filters?: FeatureFilters) {
  return useQuery({
    queryKey: ['features', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status?.length) params.append('status', filters.status.join(','));
      if (filters?.category?.length) params.append('category', filters.category.join(','));
      if (filters?.priority?.length) params.append('priority', filters.priority.join(','));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortDir) params.append('sortDir', filters.sortDir);

      const { data } = await api.get<PaginatedResponse<FeatureRequest>>(
        `/features?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useFeatureRequest(id?: string) {
  return useQuery({
    queryKey: ['features', id],
    queryFn: async () => {
      const { data } = await api.get<FeatureRequest>(`/features/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateFeatureRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      title: string;
      description: string;
      category: FeatureCategory;
      priority?: FeaturePriority;
    }) => {
      const { data } = await api.post<FeatureRequest>('/features', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
    },
  });
}

export function useUpdateFeatureRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<FeatureRequest> }) => {
      const { data } = await api.patch<FeatureRequest>(`/features/${id}`, payload);
      return data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['features', data.id] });
    },
  });
}

export function useUpvoteFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (featureId: string) => {
      const { data } = await api.post<FeatureRequest>(`/features/${featureId}/upvote`);
      return data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['features', data.id] });
    },
  });
}

export function useFeatureComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      featureId,
      body,
      isInternal,
    }: {
      featureId: string;
      body: string;
      isInternal?: boolean;
    }) => {
      const { data } = await api.post<FeatureRequest>(`/features/${featureId}/comment`, {
        body,
        isInternal,
      });
      return data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['features', data.id] });
    },
  });
}

export function useFeatureStats() {
  return useQuery({
    queryKey: ['features', 'stats'],
    queryFn: async () => {
      const { data } = await api.get<FeatureStats>('/features/stats');
      return data;
    },
  });
}

export function useMyFeatureRequests(filters?: FeatureFilters) {
  return useQuery({
    queryKey: ['features', 'my', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status?.length) params.append('status', filters.status.join(','));
      if (filters?.category?.length) params.append('category', filters.category.join(','));
      if (filters?.priority?.length) params.append('priority', filters.priority.join(','));
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const { data } = await api.get<PaginatedResponse<FeatureRequest>>(
        `/features/my?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useFeatureRoadmap() {
  return useQuery({
    queryKey: ['features', 'roadmap'],
    queryFn: async () => {
      const { data } = await api.get<Record<string, FeatureRequest[]>>('/features/roadmap');
      return data;
    },
  });
}
